/**
 * DiscountFormulaItemEditor — F3 (selective invoice gen + discount pipeline)
 *
 * Small controlled component that edits a single discount formula item.
 * Rendered by PricingFormulaEditor when item.type === 'discount', which means
 * it appears identically in BOTH hosts:
 *   - PropertyFormFields → PricingFormulaEditor → DiscountFormulaItemEditor
 *   - UnitFormPage       → PricingFormulaEditor → DiscountFormulaItemEditor
 *
 * The operator enters a POSITIVE amount (e.g. 20). Negation (-20) happens in
 * PricingEngine on the backend so the formula JSON stays readable.
 *
 * Key behaviours:
 *   - Negative input normalised to Math.abs on blur.
 *   - Zero is allowed (operator may temporarily disable a discount row without
 *     deleting it).
 *   - Empty label falls back to t('pricing_formula.default_discount_label');
 *     key is derived from the label slug. If siblingKeys already includes
 *     'discount', the key auto-suffixes to 'discount_2', 'discount_3', etc.
 *
 * Internal state mirrors the incoming `value` prop so the component can be
 * driven by an unresponsive test mock as well as a live form parent.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus, Info } from 'lucide-react'
// Import Input directly (not via @/components/ui barrel) to avoid pulling in
// SetupGuideAffordances → @/i18n in tests where localStorage is
// unavailable (happy-dom without --localstorage-file).
import { Input } from '@/components/ui/Input'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DiscountFormulaValue {
  key: string
  label: string
  type: 'discount'
  amount: number
}

export interface DiscountFormulaItemEditorProps {
  /** Current value for this discount row (drives initial / external state). */
  value: Partial<DiscountFormulaValue>
  /** Called whenever label or amount changes. Emits fully normalised value. */
  onChange: (value: DiscountFormulaValue) => void
  /**
   * Keys already used by other discount items on the same host formula.
   * Used to auto-dedupe when the operator leaves the label blank.
   */
  siblingKeys?: string[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Slugify a human label into a formula key. */
function slugify(label: string): string {
  return label.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
}

/**
 * Derive a unique key from a label string.
 * If the derived slug already exists in siblingKeys, append _2, _3, etc.
 */
function deriveKey(label: string, siblingKeys: string[]): string {
  const base = slugify(label) || 'discount'
  if (!siblingKeys.includes(base)) return base
  let n = 2
  while (siblingKeys.includes(`${base}_${n}`)) n++
  return `${base}_${n}`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DiscountFormulaItemEditor({
  value,
  onChange,
  siblingKeys = [],
}: DiscountFormulaItemEditorProps) {
  const { t } = useTranslation('common')

  const defaultLabel = t('pricing_formula.default_discount_label')

  // Internal state so the component works even when the parent doesn't
  // immediately re-render with the new value (e.g. in tests).
  const [localLabel, setLocalLabel] = useState(value.label ?? '')
  const [localAmount, setLocalAmount] = useState<number | ''>(value.amount ?? '')

  // Sync when the parent drives new value from outside (e.g. initial data load)
  useEffect(() => {
    setLocalLabel(value.label ?? '')
    setLocalAmount(value.amount ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Build and emit the normalised value from current local state. */
  const emitCurrent = (label: string, amount: number | '') => {
    const resolvedLabel = label.trim() || defaultLabel
    const key = deriveKey(resolvedLabel, siblingKeys)
    const resolvedAmount = typeof amount === 'number' ? amount : 0
    onChange({ key, label: resolvedLabel, type: 'discount', amount: resolvedAmount })
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLabel = e.target.value
    setLocalLabel(newLabel)
    emitCurrent(newLabel, localAmount)
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Parse raw input — negative is allowed at typing time; normalised on blur.
    const raw = e.target.value === '' ? '' : Number(e.target.value)
    setLocalAmount(raw)
    if (raw !== '') {
      emitCurrent(localLabel, raw)
    }
  }

  const handleAmountBlur = () => {
    // Normalise negative values to positive on blur. Engine handles negation.
    const current = typeof localAmount === 'number' ? localAmount : 0
    const normalised = Math.abs(current)
    if (normalised !== current) {
      setLocalAmount(normalised)
      emitCurrent(localLabel, normalised)
    }
  }

  const hintText = t('pricing_formula.discount_hint')

  return (
    <div className="space-y-2">
      {/* Label input (optional) */}
      <Input
        type="text"
        placeholder={t('pricing_formula.discount_label_placeholder')}
        value={localLabel}
        onChange={handleLabelChange}
        aria-label={t('pricing_formula.discount_label_aria')}
      />

      {/* Amount input with minus-sign visual cue + warning colour */}
      <div className="flex items-center gap-2 w-full max-w-xs">
        <div className="flex-1">
          <Input
            type="number"
            step="0.01"
            min="0"
            placeholder={t('pricing_formula.discount_amount_placeholder')}
            value={localAmount}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
            // Minus prefix communicates "this REDUCES the total"
            leftIcon={<Minus className="w-3 h-3 text-warning" />}
            aria-label={t('pricing_formula.discount_amount_aria')}
            inputClassName="text-warning"
          />
        </div>
        {/* Tooltip / hint */}
        <div
          className="tooltip tooltip-left shrink-0"
          data-tip={hintText}
        >
          <Info className="w-4 h-4 text-base-content/40 cursor-help" aria-label={hintText} />
        </div>
      </div>
    </div>
  )
}
